import React, { useEffect, useState } from 'react'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import { FormControl, Dialog } from '@mui/material'
import Select, { FormatOptionLabelMeta } from 'react-select'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { SubDomain } from '../../types/subDomainTypes'
import {
  getSubDomainbySecurityControls,
  updateSubDomain,
} from '../../redux/subDomains/subDomainActions'
import FixWeightTargetScore from '../containers/FixWeightTargetScoreForm'
import UseAlert from './alert'
import { DetailButton } from './detail'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'
import './detail/detail.css'
import './selectSubDomain.css'
import '../../i18n'

interface SelectSubDomainProps {
  securityControlIds: string | string[]
  selectedSubDomainIds: string | string[]
  onChange: (selectedSubDomainIds: string | string[]) => void
  multiple?: boolean
}

type SubDomainOption = {
  value: string
  label: string
  needsFix: boolean
  subDomain?: SubDomain
}

const needsWeightFix = (sd?: SubDomain | null) =>
  !!sd && sd.TargetScore === -1 && sd.Weight === -1

const SelectSubDomain: React.FC<SelectSubDomainProps> = ({
  securityControlIds,
  selectedSubDomainIds,
  onChange,
  multiple = true,
}) => {
  const menu = useExclusiveMenu()
  const dispatch: AppDispatch = useAppDispatch()
  const { t } = useTranslation()
  const subDomainsbySecurity = useSelector(
    (state: RootState) => state.subDomain.subDomainsbySecurityControls
  )

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })
  const [subDomains, setSubDomains] = useState<SubDomain[]>([])
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [openForm, setOpenForm] = useState(false)
  const [selectedSubDomain, setSelectedSubDomain] = useState<SubDomain | null>(
    null
  )

  const SecurityControlIdsString = Array.isArray(securityControlIds)
    ? securityControlIds.join(',')
    : securityControlIds

  useEffect(() => {
    if (!SecurityControlIdsString) {
      setOptionsLoading(false)
      return
    }
    let active = true
    const fetchSubDomains = async () => {
      setOptionsLoading(true)
      try {
        await dispatch(
          getSubDomainbySecurityControls(SecurityControlIdsString) as any
        )
      } catch (error) {
        console.error('Error fetching subdomains:', error)
      } finally {
        if (active) setOptionsLoading(false)
      }
    }
    fetchSubDomains()
    return () => {
      active = false
    }
  }, [SecurityControlIdsString, dispatch, selectedSubDomain])

  useEffect(() => {
    setSubDomains(subDomainsbySecurity || [])
  }, [subDomainsbySecurity])

  const openSetupFor = (subDomain: SubDomain) => {
    setSelectedSubDomain(subDomain)
    setOpenForm(true)
  }

  const handleWarningClick = (
    e: React.MouseEvent,
    subDomain: SubDomain
  ) => {
    e.preventDefault()
    e.stopPropagation()
    openSetupFor(subDomain)
  }

  const handleFormCancel = () => {
    setOpenForm(false)
  }

  const handleUpdateSubDomain = async () => {
    if (
      !selectedSubDomain ||
      selectedSubDomain.Weight == null ||
      selectedSubDomain.TargetScore == null ||
      selectedSubDomain.Weight < 0 ||
      selectedSubDomain.TargetScore < 0
    ) {
      setAlert({
        open: true,
        message: t('alerts.subDomainUpdate.emptyFields'),
        severity: 'error',
      })
      return
    }

    try {
      const updatedSubDomain = await dispatch(
        updateSubDomain({
          updatedSubDomain: {
            _id: selectedSubDomain._id,
            SubDomainId: selectedSubDomain.SubDomainId,
            Description: selectedSubDomain.Description,
            Weight: selectedSubDomain.Weight,
            TargetScore: selectedSubDomain.TargetScore,
            Label: selectedSubDomain.Label,
            SecurityControlId: selectedSubDomain.SecurityControlId,
            LabelSCF: selectedSubDomain.LabelSCF,
          },
          _id: selectedSubDomain._id as string,
        })
      ).unwrap()

      setSelectedSubDomain((prev) => ({
        ...prev,
        ...updatedSubDomain,
      }))

      const currentIds = Array.isArray(selectedSubDomainIds)
        ? selectedSubDomainIds
        : [selectedSubDomainIds]
      if (!currentIds.includes(updatedSubDomain._id as string)) {
        onChange(
          multiple
            ? [...currentIds, updatedSubDomain._id as string]
            : (updatedSubDomain._id as string)
        )
      }

      setAlert({
        open: true,
        message: t('alerts.subDomainUpdate.success'),
        severity: 'success',
      })
      handleFormCancel()
    } catch (error: any) {
      console.error('Error updating SUB DOMAIN:', error.message)
      setAlert({
        open: true,
        message: t('alerts.subDomainUpdate.error'),
        severity: 'error',
      })
    }
  }

  const containerRef = React.useRef<HTMLDivElement | null>(null)


  const options: SubDomainOption[] = subDomains.map((subDomain) => ({
    value: subDomain._id || '',
    label: subDomain.LabelSD || '',
    needsFix: needsWeightFix(subDomain),
    subDomain,
  }))

  const selectedOptions = multiple
    ? options.filter(
        (opt) =>
          Array.isArray(selectedSubDomainIds) &&
          selectedSubDomainIds.includes(opt.value)
      )
    : options.find((opt) => opt.value === selectedSubDomainIds) || null

  const selectedNeedsFix = multiple
    ? (selectedOptions as SubDomainOption[]).find((o) => o.needsFix) || null
    : selectedOptions && (selectedOptions as SubDomainOption).needsFix
      ? (selectedOptions as SubDomainOption)
      : null

  const handleSelectChange = (selected: any) => {
    if (multiple) {
      const list = Array.isArray(selected) ? selected : []
      onChange(list.map((s: any) => s.value))
      const incomplete = list.find((s: SubDomainOption) => s.needsFix)
      if (incomplete?.subDomain) {
        openSetupFor(incomplete.subDomain)
      }
    } else if (selected && !Array.isArray(selected)) {
      onChange(selected.value)
      if (selected.needsFix && selected.subDomain) {
        openSetupFor(selected.subDomain)
      }
    } else {
      onChange('')
    }
  }

  const formatOptionLabel = (
    option: SubDomainOption,
    meta: FormatOptionLabelMeta<SubDomainOption>
  ) => {
    const sd =
      option.subDomain ||
      subDomains.find((s) => s._id === option.value) ||
      null
    const showAlert = needsWeightFix(sd)
    const isValue = meta.context === 'value'

    return (
      <div className={`incyia-sd-option${isValue ? ' is-value' : ''}`}>
        <span className="incyia-sd-option-label" title={option.label}>
          {option.label}
        </span>
        {showAlert && sd ? (
          <button
            type="button"
            className={`incyia-sd-setup-chip${isValue ? ' is-value' : ''}`}
            title={t('popups.fixWeightTargetScore.guideCta')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => handleWarningClick(e, sd)}
          >
            <ErrorOutlineRoundedIcon sx={{ fontSize: 14 }} />
            <span>{t('popups.fixWeightTargetScore.needsSetup')}</span>
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />
      <div ref={containerRef} className="incyia-sd-wrap">
        <FormControl fullWidth style={{ width: '100%' }}>
          <Select
            isMulti={multiple}
            isClearable={!multiple}
            value={selectedOptions}
            onChange={handleSelectChange}
            options={options}
            isLoading={optionsLoading}
            placeholder={optionsLoading ? t('selectOptions.loading') : t('SubDomains')}
            isDisabled={!securityControlIds}
            noOptionsMessage={() =>
              securityControlIds
                ? t('No subdomains available')
                : t('Please select a Security Control first')
            }
            formatOptionLabel={formatOptionLabel}
            {...menu}
            styles={incyiaSelectStyles}
            classNamePrefix="incyia-sd"
          />
        </FormControl>

        {selectedNeedsFix?.subDomain ? (
          <div className="incyia-sd-guide" role="status">
            <div className="incyia-sd-guide-ico" aria-hidden>
              <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />
            </div>
            <div className="incyia-sd-guide-copy">
              <p className="incyia-sd-guide-title">
                {t('popups.fixWeightTargetScore.guideTitle')}
              </p>
              <p className="incyia-sd-guide-body">
                {t('popups.fixWeightTargetScore.guideBody')}
              </p>
            </div>
            <DetailButton
              size="sm"
              onClick={() => openSetupFor(selectedNeedsFix.subDomain!)}
            >
              {t('popups.fixWeightTargetScore.guideCta')}
            </DetailButton>
          </div>
        ) : null}
      </div>

      <Dialog
        open={openForm}
        onClose={handleFormCancel}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: 'incyia-modal-paper' }}
      >
        <div className="incyia-modal-head">
          <div>
            <h3>
              {t('popups.fixWeightTargetScore.title', {
                defaultValue: t('Update SubDomain'),
              })}
            </h3>
            <p>
              {t('popups.fixWeightTargetScore.subtitle')}
              {selectedSubDomain?.LabelSD || selectedSubDomain?.Label
                ? ` — ${selectedSubDomain.LabelSD || selectedSubDomain.Label}`
                : ''}
            </p>
          </div>
          <button
            type="button"
            className="incyia-modal-x"
            onClick={handleFormCancel}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
        <div className="incyia-modal-body">
          {selectedSubDomain ? (
            <FixWeightTargetScore
              values={selectedSubDomain}
              onChange={(e: any) => {
                const { name, value } = e.target
                const numericValue = Number(value)
                if (name === 'Weight' && numericValue > 4) return
                if (name === 'TargetScore' && numericValue > 5) return
                setSelectedSubDomain(
                  (prev) => prev && { ...prev, [name]: numericValue }
                )
              }}
              onSubmit={handleUpdateSubDomain}
              onCancel={handleFormCancel}
              fields={[
                {
                  type: 'number',
                  label: t('popups.fixWeightTargetScore.weight', {
                    defaultValue: 'Weight',
                  }),
                  name: 'Weight',
                  required: true,
                },
                {
                  type: 'number',
                  label: t('popups.fixWeightTargetScore.targetScore', {
                    defaultValue: 'Target Score',
                  }),
                  name: 'TargetScore',
                  required: true,
                },
              ]}
            />
          ) : null}
        </div>
      </Dialog>
    </>
  )
}

export default SelectSubDomain
